import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ConfirmDialog, type ConfirmDialogVariant } from './ConfirmDialog';
import { PromptDialog } from './PromptDialog';

export interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
}

export interface PromptOptions {
  title?: string;
  message: ReactNode;
  defaultValue?: string;
  placeholder?: string;
  inputLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  minLength?: number;
  maxLength?: number;
  /** When set, confirm stays disabled until input matches exactly. */
  expectedValue?: string;
}

interface DialogContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

interface PendingConfirm extends ConfirmOptions {
  kind: 'confirm';
  resolve: (value: boolean) => void;
}

interface PendingPrompt extends PromptOptions {
  kind: 'prompt';
  resolve: (value: string | null) => void;
}

type PendingDialog = PendingConfirm | PendingPrompt;

/**
 * App-wide confirm / prompt dialogs (replaces window.confirm / window.prompt).
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingDialog | null>(null);
  const pendingRef = useRef<PendingDialog | null>(null);

  const clearPending = useCallback(() => {
    pendingRef.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      if (pendingRef.current) {
        if (pendingRef.current.kind === 'confirm') {
          pendingRef.current.resolve(false);
        } else {
          pendingRef.current.resolve(null);
        }
      }
      const next: PendingConfirm = { ...options, kind: 'confirm', resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const prompt = useCallback((options: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      if (pendingRef.current) {
        if (pendingRef.current.kind === 'confirm') {
          pendingRef.current.resolve(false);
        } else {
          pendingRef.current.resolve(null);
        }
      }
      const next: PendingPrompt = { ...options, kind: 'prompt', resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const value = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <ConfirmDialog
        isOpen={pending?.kind === 'confirm'}
        title={pending?.kind === 'confirm' ? pending.title : undefined}
        message={pending?.kind === 'confirm' ? pending.message : ''}
        confirmLabel={pending?.kind === 'confirm' ? pending.confirmLabel : undefined}
        cancelLabel={pending?.kind === 'confirm' ? pending.cancelLabel : undefined}
        variant={pending?.kind === 'confirm' ? pending.variant : undefined}
        onConfirm={() => {
          if (pendingRef.current?.kind !== 'confirm') return;
          const current = pendingRef.current;
          clearPending();
          current.resolve(true);
        }}
        onCancel={() => {
          if (pendingRef.current?.kind !== 'confirm') return;
          const current = pendingRef.current;
          clearPending();
          current.resolve(false);
        }}
      />
      <PromptDialog
        isOpen={pending?.kind === 'prompt'}
        title={pending?.kind === 'prompt' ? pending.title : undefined}
        message={pending?.kind === 'prompt' ? pending.message : ''}
        defaultValue={pending?.kind === 'prompt' ? pending.defaultValue : ''}
        placeholder={pending?.kind === 'prompt' ? pending.placeholder : undefined}
        inputLabel={pending?.kind === 'prompt' ? pending.inputLabel : undefined}
        confirmLabel={pending?.kind === 'prompt' ? pending.confirmLabel : undefined}
        cancelLabel={pending?.kind === 'prompt' ? pending.cancelLabel : undefined}
        variant={pending?.kind === 'prompt' ? pending.variant : undefined}
        minLength={pending?.kind === 'prompt' ? pending.minLength : undefined}
        maxLength={pending?.kind === 'prompt' ? pending.maxLength : undefined}
        expectedValue={pending?.kind === 'prompt' ? pending.expectedValue : undefined}
        onConfirm={(value) => {
          if (pendingRef.current?.kind !== 'prompt') return;
          const current = pendingRef.current;
          clearPending();
          current.resolve(value);
        }}
        onCancel={() => {
          if (pendingRef.current?.kind !== 'prompt') return;
          const current = pendingRef.current;
          clearPending();
          current.resolve(null);
        }}
      />
    </DialogContext.Provider>
  );
}

function useDialogContext(): DialogContextValue {
  const context = useContext(DialogContext);
  if (context === undefined) {
    throw new Error('useConfirm / usePrompt must be used within a ConfirmProvider');
  }
  return context;
}

export function useConfirm(): DialogContextValue['confirm'] {
  return useDialogContext().confirm;
}

export function usePrompt(): DialogContextValue['prompt'] {
  return useDialogContext().prompt;
}
